import { hasAccess } from "@woven/contracts";
import { ArrowRight, BrainCircuit, Database, Radar } from "lucide-react";
import { Link } from "react-router";
import { useAuth } from "../auth/context";
import { DashboardPreview } from "../components/marketing/DashboardPreview";
import { OpenSourceBanner } from "../components/marketing/OpenSourceBanner";
import { LowPolyTerrain } from "../components/ui/LowPolyTerrain";

const FEATURES = [
  {
    title: "Connect",
    body: "Use Rust and TypeScript SDKs over native QUIC and browser WebTransport.",
    icon: BrainCircuit,
  },
  {
    title: "Provision",
    body: "Configure managed servers, session scopes, and capacity through the Woven Host control plane.",
    icon: Radar,
  },
  {
    title: "Monitor",
    body: "Observe server status, capacity, connections, and recent client logs in one console.",
    icon: Database,
  },
] as const;

export function HomePage() {
  const { user, loading, access, accessRequested, error } = useAuth();
  const canOpenConsole = Boolean(
    user && !loading && !error && accessRequested !== null && hasAccess(access, "lite")
  );

  return (
    <>
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-copy">
            <h1 className="hero-title">
              Realtime infrastructure,
              <br />
              <span className="hero-title-accent">woven together.</span>
            </h1>
            <p className="hero-body">
              Woven provides product-agnostic realtime infrastructure for games, simulations, and
              interactive apps through sessions, events, and spatial routing.
            </p>
            <div className="hero-actions">
              {canOpenConsole ? (
                <Link to="/console" className="btn btn--primary btn--lg">
                  Open Console
                </Link>
              ) : (
                <>
                  <Link
                    to="/sign-up"
                    state={{ initialInterest: "lite" }}
                    className="btn btn--primary btn--lg"
                  >
                    {user ? "Request Access" : "Start Building"}
                  </Link>
                  <Link to="/docs" className="btn btn--text btn--lg">
                    Read the Docs
                    <ArrowRight size={18} strokeWidth={2} absoluteStrokeWidth aria-hidden="true" />
                  </Link>
                </>
              )}
            </div>
          </div>
          <div className="hero-field">
            <LowPolyTerrain />
          </div>
        </div>
      </section>

      <div className="page">
        <section className="feature-grid" aria-label="What Woven does">
          {FEATURES.map((feature) => (
            <article className="panel feature-card" key={feature.title}>
              <feature.icon
                className="feature-mark"
                size={22}
                strokeWidth={1.5}
                absoluteStrokeWidth
                aria-hidden="true"
              />
              <h2 className="feature-title">{feature.title}</h2>
              <p className="feature-body">{feature.body}</p>
            </article>
          ))}
        </section>

        <OpenSourceBanner />

        <section aria-label="Console preview">
          <DashboardPreview />
        </section>
      </div>
    </>
  );
}
